import { Drawer, Box, IconButton, Stack } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { DrawerNavMenu } from "./DrawerNavMenu";
import type { NavItems } from "./navItems";
import { ThemeSwitch } from "./ThemeSwitch";
import { Logo } from "../logo/Logo";
import { AuthUserMenu } from "src/infrastructure/auth";
import { useDrawer } from "src/utils/useDrawer";
import { useTheme } from "@mui/material";

const DRAWER_WIDTH_EXPANDED = 256;
const DRAWER_WIDTH_COLLAPSED = 64;

/**
 * Desktop navigation drawer (permanent sidebar).
 *
 * Features:
 * - Collapsible/expandable
 * - Logo and app name at top
 * - Navigation menu items
 * - Theme toggle
 * - User profile at bottom
 */
export function DrawerDesktop({ navItems }: { navItems: NavItems }) {
  const { isOpen, toggle } = useDrawer();
  const theme = useTheme();

  return (
    <>
      <Drawer
        variant="permanent"
        open={isOpen}
        sx={{
          width: isOpen ? DRAWER_WIDTH_EXPANDED : DRAWER_WIDTH_COLLAPSED,
          flexShrink: 0,
          whiteSpace: "nowrap",
          boxSizing: "border-box",
          "& .MuiDrawer-paper": {
            width: isOpen ? DRAWER_WIDTH_EXPANDED : DRAWER_WIDTH_COLLAPSED,
            boxSizing: "border-box",
            overflowX: "hidden",
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: isOpen
                ? theme.transitions.duration.enteringScreen
                : theme.transitions.duration.leavingScreen,
            }),
          },
        }}
      >
        <Stack flexGrow={1} ml={1} mr={0.5}>
          {/* Logo/Header */}
          <Box ml={1} mt={0.5} mb={1.5}>
            <Logo height={64} dark={theme.palette.mode === "dark"} />
          </Box>

          {/* Navigation Menu */}
          <DrawerNavMenu items={navItems} mr={0.5} flexGrow={1} />

          {/* Theme Switch */}
          <ThemeSwitch collapsed={!isOpen} />

          <AuthUserMenu collapsed={!isOpen} />
        </Stack>
      </Drawer>

      {/* Toggle Button (positioned outside drawer) */}
      <Box
        sx={{
          position: "fixed",
          left: isOpen ? DRAWER_WIDTH_EXPANDED : DRAWER_WIDTH_COLLAPSED,
          top: "20px",
          transform: "translate(-50%, 0)",
          zIndex: theme.zIndex.drawer,
          transition: theme.transitions.create("left", {
            easing: theme.transitions.easing.sharp,
            duration: isOpen
              ? theme.transitions.duration.enteringScreen
              : theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <IconButton
          onClick={toggle}
          sx={{
            width: 24,
            height: 24,
            boxShadow: 4,
            backgroundColor: "background.paper",
            border:
              theme.palette.mode === "light"
                ? undefined
                : `1px solid ${theme.palette.action.disabledBackground}`,
            "&:hover": {
              backgroundColor: "background.paper",
            },
          }}
        >
          {isOpen ? <ChevronLeft /> : <ChevronRight />}
        </IconButton>
      </Box>
    </>
  );
}
